export enum MOTIFS {
  spring = 'spring',
  summer = 'summer',
  fall_autumn = 'fall_autumn',
  winter = 'winter',
  floral = 'floral',
  greenery = 'greenery',
  rustic = 'rustic',
  destination = 'destination',
  beach = 'beach',
  modern = 'modern',
  elegant_formal = 'elegant_formal',
  simple_minimalist = 'simple_minimalist',
  typography = 'typography',
  classic_traditional = 'classic_traditional',
  monogram = 'monogram',
  boho = 'boho',
  mountains = 'mountains',
  vintage_retro = 'vintage_retro',
  maximalist = 'maximalist',
  nautical = 'nautical',
  barn_country = 'barn_country',
  whimsical = 'whimsical',
  border = 'border',
  geometric = 'geometric',
  cultural = 'cultural',
  watercolor = 'watercolor',
  disney = 'disney',
}

export enum COLORS {
  brown = 'brown',
  peach = 'peach',
  beige = 'beige',
  multi = 'multi',
  yellow = 'yellow',
  rose = 'rose',
  dusty_rose = 'dusty_rose',
  grey = 'grey',
  orange = 'orange',
  black = 'black',
  blue = 'blue',
  burgundy = 'burgundy',
  coral = 'coral',
  dusty_blue = 'dusty_blue',
  emerald_green = 'emerald_green',
  gold = 'gold',
  green = 'green',
  lavender = 'lavender',
  navy = 'navy',
  pink = 'pink',
  plum = 'plum',
  purple = 'purple',
  red = 'red',
  rose_gold = 'rose_gold',
  royal_blue = 'royal_blue',
  silver = 'silver',
  teal = 'teal',
  white = 'white',
}

export enum SHAPES {
  square = 'square',
  rounded = 'rounded',
  scalloped = 'scalloped',
  diamond = 'diamond',
  ticket = 'ticket',
  arch = 'arch',
}

export enum OCCASIONS {
  hanukkah = 'hanukkah',
  kwanzaa = 'kwanzaa',
  christmas = 'christmas',
  new_year = 'new_year',
  seasons_greetings = 'seasons_greetings',
  disney_holiday = 'disney_holiday',
}

export enum FEATURES {
  has_foil = 'has_foil',
  has_magnet = 'has_magnet',
  has_custom_photo = 'has_custom_photo',
  has_no_custom_photo = 'has_no_custom_photo',
  is_letterpress = 'is_letterpress',
  is_vow_renewal = 'is_vow_renewal',
}

export enum ORIENTATIONS {
  is_portrait = 'is_portrait',
  is_landscape = 'is_landscape',
}

export type AllFilters = MOTIFS | COLORS | SHAPES | OCCASIONS | FEATURES | ORIENTATIONS;

type FilterTemplateVariables = {
  [key in AllFilters]: {
    /** The filter's name (in title case). Used in multiple meta fields. */
    label: string;
    /** Strings used as a prefixes for keyword strings (e.g. "KEYWORD thank you cards") */
    keywords: string[];
    /** String appended to the base URL to generate a canonical URL */
    urlSuffix: string;
  };
};

export const FILTER_TEMPLATE_VARIABLES: FilterTemplateVariables = {
  [MOTIFS.spring]: {
    label: 'Spring',
    keywords: ['spring', 'spring themed'],
    urlSuffix: '/spring',
  },
  [MOTIFS.summer]: {
    label: 'Summer',
    keywords: ['summer', 'summer themed'],
    urlSuffix: '/summer',
  },
  [MOTIFS.fall_autumn]: {
    label: 'Fall & Autumn',
    keywords: ['fall', 'autumn', 'fall themed'],
    urlSuffix: '/fall_autumn',
  },
  [MOTIFS.winter]: {
    label: 'Winter',
    keywords: ['winter', 'winter wonderland', 'winter themed'],
    urlSuffix: '/winter',
  },
  [MOTIFS.floral]: {
    label: 'Floral',
    keywords: ['floral', 'flower', 'floral themed'],
    urlSuffix: '/floral',
  },
  [MOTIFS.greenery]: {
    label: 'Greenery',
    keywords: ['greenery', 'botanical', 'eucalyptus', 'nature'],
    urlSuffix: '/greenery',
  },
  [MOTIFS.rustic]: {
    label: 'Rustic',
    keywords: ['rustic', 'country', 'rustic country', 'rustic chic'],
    urlSuffix: '/rustic',
  },
  [MOTIFS.destination]: {
    label: 'Destination Themed',
    keywords: ['destination', 'destination themed', 'travel', 'travel themed'],
    urlSuffix: '/destination',
  },
  [MOTIFS.beach]: {
    label: 'Beach Themed',
    keywords: ['beach', 'beach themed', 'tropical', 'ocean themed'],
    urlSuffix: '/beach',
  },
  [MOTIFS.modern]: {
    label: 'Modern & Contemporary',
    keywords: ['modern', 'contemporary', 'modern unique'],
    urlSuffix: '/modern',
  },
  [MOTIFS.elegant_formal]: {
    label: 'Elegant & Formal',
    keywords: ['elegant', 'formal', 'refined'],
    urlSuffix: '/elegant_formal',
  },
  [MOTIFS.simple_minimalist]: {
    label: 'Simple & Minimalist',
    keywords: ['simple', 'minimalist', 'basic', 'simple elegant'],
    urlSuffix: '/simple_minimalist',
  },
  [MOTIFS.typography]: {
    label: 'Typography',
    keywords: ['typography'],
    urlSuffix: '/typography',
  },
  [MOTIFS.classic_traditional]: {
    label: 'Classic & Traditional',
    keywords: ['classic', 'traditional'],
    urlSuffix: '/classic_traditional',
  },
  [MOTIFS.monogram]: {
    label: 'Monogram',
    keywords: ['monogram', 'monogrammed'],
    urlSuffix: '/monogram',
  },
  [MOTIFS.boho]: {
    label: 'Boho',
    keywords: ['boho', 'bohemian', 'boho chic'],
    urlSuffix: '/boho',
  },
  [MOTIFS.mountains]: {
    label: 'Mountain',
    keywords: ['mountains', 'mountain themed', 'alpine'],
    urlSuffix: '/mountains',
  },
  [MOTIFS.vintage_retro]: {
    label: 'Vintage & Retro',
    keywords: ['vintage', 'retro', 'vintage style'],
    urlSuffix: '/vintage_retro',
  },
  [MOTIFS.maximalist]: {
    label: 'Maximalist',
    keywords: ['maximalist'],
    urlSuffix: '/maximalist',
  },
  [MOTIFS.nautical]: {
    label: 'Nautical',
    keywords: ['nautical'],
    urlSuffix: '/nautical',
  },
  [MOTIFS.barn_country]: {
    label: 'Barn & Country',
    keywords: ['barn', 'country', 'rural'],
    urlSuffix: '/barn_country',
  },
  [MOTIFS.whimsical]: {
    label: 'Whimsical',
    keywords: ['whimsical', 'whimsy'],
    urlSuffix: '/whimsical',
  },
  [MOTIFS.border]: {
    label: 'Bordered',
    keywords: ['bordered', 'border'],
    urlSuffix: '/border',
  },
  [MOTIFS.geometric]: {
    label: 'Geometric',
    keywords: ['geometric'],
    urlSuffix: '/geometric',
  },
  [MOTIFS.cultural]: {
    label: 'Cultural',
    keywords: ['cultural'],
    urlSuffix: '/cultural',
  },
  [MOTIFS.watercolor]: {
    label: 'Watercolor',
    keywords: ['watercolor'],
    urlSuffix: '/watercolor',
  },
  [MOTIFS.disney]: {
    label: 'Disney',
    keywords: ['disney', 'disney themed'],
    urlSuffix: '/disney',
  },
  [COLORS.brown]: {
    label: 'Brown',
    keywords: ['brown'],
    urlSuffix: '/brown',
  },
  [COLORS.peach]: {
    label: 'Peach',
    keywords: ['peach', 'peach colored'],
    urlSuffix: '/peach',
  },
  [COLORS.beige]: {
    label: 'Beige',
    keywords: ['beige'],
    urlSuffix: '/beige',
  },
  [COLORS.multi]: {
    label: 'Multi-Colored',
    keywords: ['multicolor', 'multicolored', 'colorful'],
    urlSuffix: '/multi',
  },
  [COLORS.yellow]: {
    label: 'Yellow',
    keywords: ['yellow', 'bright yellow'],
    urlSuffix: '/yellow',
  },
  [COLORS.rose]: {
    label: 'Rose',
    keywords: ['rose', 'rose colored'],
    urlSuffix: '/rose',
  },
  [COLORS.dusty_rose]: {
    label: 'Dusty Rose',
    keywords: ['dusty rose'],
    urlSuffix: '/dusty_rose',
  },
  [COLORS.grey]: {
    label: 'Grey',
    keywords: ['grey', 'gray'],
    urlSuffix: '/grey',
  },
  [COLORS.orange]: {
    label: 'Orange',
    keywords: ['orange', 'orange colored'],
    urlSuffix: '/orange',
  },
  [COLORS.black]: {
    label: 'Black',
    keywords: ['black', 'black background', 'black vintage'],
    urlSuffix: '/black',
  },
  [COLORS.blue]: {
    label: 'Blue',
    keywords: ['blue', 'light blue', 'tiffany blue'],
    urlSuffix: '/blue',
  },
  [COLORS.burgundy]: {
    label: 'Burgundy',
    keywords: ['burgundy', 'dark red'],
    urlSuffix: '/burgundy',
  },
  [COLORS.coral]: {
    label: 'Coral',
    keywords: ['coral', 'coral colored'],
    urlSuffix: '/coral',
  },
  [COLORS.dusty_blue]: {
    label: 'Dusty Blue',
    keywords: ['dusty_blue'],
    urlSuffix: '/dusty_blue',
  },
  [COLORS.emerald_green]: {
    label: 'Emerald Green',
    keywords: ['emerald_green', 'dark green'],
    urlSuffix: '/emerald_green',
  },
  [COLORS.gold]: {
    label: 'Gold',
    keywords: ['gold', 'gold foil'],
    urlSuffix: '/gold',
  },
  [COLORS.green]: {
    label: 'Green',
    keywords: ['green'],
    urlSuffix: '/green',
  },
  [COLORS.lavender]: {
    label: 'Lavender',
    keywords: ['lavender', 'light purple', 'lavender flower'],
    urlSuffix: '/lavender',
  },
  [COLORS.navy]: {
    label: 'Navy Blue',
    keywords: ['navy', 'navy blue'],
    urlSuffix: '/navy',
  },
  [COLORS.pink]: {
    label: 'Pink',
    keywords: ['pink', 'dusty pink', 'pink silver', 'light pink'],
    urlSuffix: '/pink',
  },
  [COLORS.plum]: {
    label: 'Plum',
    keywords: ['plum', 'plum colored'],
    urlSuffix: '/plum',
  },
  [COLORS.purple]: {
    label: 'Purple',
    keywords: ['purple', 'deep purple'],
    urlSuffix: '/purple',
  },
  [COLORS.red]: {
    label: 'Red',
    keywords: ['red', 'elegant red', 'red themed'],
    urlSuffix: '/red',
  },
  [COLORS.rose_gold]: {
    label: 'Rose Gold',
    keywords: ['rose gold', 'rose gold foil', 'elegant rose gold', 'rose gold geometric'],
    urlSuffix: '/rose_gold',
  },
  [COLORS.royal_blue]: {
    label: 'Royal Blue',
    keywords: ['royal blue'],
    urlSuffix: '/royal_blue',
  },
  [COLORS.silver]: {
    label: 'Silver',
    keywords: ['silver', 'silver foil'],
    urlSuffix: '/silver',
  },
  [COLORS.teal]: {
    label: 'Teal',
    keywords: ['teal', 'rustic teal', 'dark teal'],
    urlSuffix: '/teal',
  },
  [COLORS.white]: {
    label: 'White',
    keywords: ['white', 'pearl white'],
    urlSuffix: '/white',
  },
  [SHAPES.square]: {
    label: 'Square',
    keywords: ['square', 'square shaped'],
    urlSuffix: '/square',
  },
  [SHAPES.rounded]: {
    label: 'Rounded',
    keywords: ['rounded'],
    urlSuffix: '/rounded',
  },
  [SHAPES.scalloped]: {
    label: 'Scalloped',
    keywords: ['scalloped'],
    urlSuffix: '/scalloped',
  },
  [SHAPES.diamond]: {
    label: 'Diamond',
    keywords: ['diamond', 'diamond shaped'],
    urlSuffix: '/diamond',
  },
  [SHAPES.ticket]: {
    label: 'Ticket',
    keywords: ['ticket', 'ticket shaped'],
    urlSuffix: '/ticket',
  },
  [SHAPES.arch]: {
    label: 'Arch',
    keywords: ['arch', 'arch shaped'],
    urlSuffix: '/arch',
  },
  [OCCASIONS.hanukkah]: {
    label: 'Hanukkah',
    keywords: ['hanukkah'],
    urlSuffix: '/hanukkah',
  },
  [OCCASIONS.kwanzaa]: {
    label: 'Kwanzaa',
    keywords: ['kwanzaa'],
    urlSuffix: '/kwanzaa',
  },
  [OCCASIONS.christmas]: {
    label: 'Christmas',
    keywords: ['christmas', 'xmas'],
    urlSuffix: '/christmas',
  },
  [OCCASIONS.seasons_greetings]: {
    label: "Season's Greetings",
    keywords: ["season's greetings"],
    urlSuffix: '/seasons_greetings',
  },
  [OCCASIONS.new_year]: {
    label: 'New Year',
    keywords: ['new year'],
    urlSuffix: '/new_year',
  },
  [OCCASIONS.disney_holiday]: {
    label: 'Disney Holiday',
    keywords: ['disney holiday', 'disney christmas'],
    urlSuffix: '/disney_holiday',
  },
  [FEATURES.has_foil]: {
    label: 'Foil',
    keywords: ['foil', 'stamped foil', 'gold foil', 'silver foil'],
    urlSuffix: '?features=foil',
  },
  [FEATURES.has_magnet]: {
    label: 'Magnet',
    keywords: ['magnet', 'magnetic'],
    urlSuffix: '?features=magnet',
  },
  [FEATURES.has_custom_photo]: {
    label: 'Photo',
    keywords: ['photo', 'custom photo', 'picture'],
    urlSuffix: '?features=customphoto',
  },
  [FEATURES.has_no_custom_photo]: {
    label: 'Non-Photo',
    keywords: ['non photo', 'photoless'],
    urlSuffix: '?features=nocustomphoto',
  },
  [FEATURES.is_letterpress]: {
    label: 'Letterpress',
    keywords: ['letterpress'],
    urlSuffix: '?is_letterpress=is_letterpress',
  },
  [FEATURES.is_vow_renewal]: {
    label: 'Reception Only',
    keywords: ['reception only', 'vow renewal', 'elopement'],
    urlSuffix: '?receptionOnly=elopementandvowrenewal',
  },
  [ORIENTATIONS.is_portrait]: {
    label: 'Portrait',
    keywords: ['portrait', 'vertical', 'tall'],
    urlSuffix: '?orientations=portrait',
  },
  [ORIENTATIONS.is_landscape]: {
    label: 'Landscape',
    keywords: ['landscape', 'horizontal', 'wide'],
    urlSuffix: '?orientations=landscape',
  },
};
